import '../Style/RecruitersPage.css';
import React, { useState } from 'react';

const productPreviews = [
  {
    title: 'Login & Onboarding',
    description: 'Users can sign in and access a personalized social goal-tracking experience.',
    images: [
      {
        src: '/images/login-preview.png',
        alt: 'Global Legends login page',
      },
      {
        src: '/images/onboarding-preview.png',
        alt: 'Global Legends login page',
      },
    ],
  },
  {
    title: 'Social Activity Feed',
    description:
      'Users can view progress updates from others, react to activity, and stay motivated through accountability.',
    images: [
      {
        src: '/images/social-activity-preview.png',
        alt: 'Global Legends social activity feed',
      },
      {
        src: '/images/reactions-preview.png',
        alt: 'Global Legends reactions modal',
      },
    ],
  },
  {
    title: 'Public Profile & Progress Overview',
    description:
      'Profiles show user rank, activity history, followers, following, and public goals.',
    images: [
      {
        src: '/images/profile-progress-preview.png',
        alt: 'Global Legends public profile and progress overview',
      },
      {
        src: '/images/public-goals-preview.png',
        alt: 'Global Legends public goals section',
      },
      {
        src: '/images/followers-preview.png',
        alt: 'Global Legends followers and following modal',
      },
    ],
  },
  {
    title: 'Goal Tracking Details',
    description: 'Users can manage individual goals, update progress, and view completion history.',
    images: [
      {
        src: '/images/goal-details-preview.png',
        alt: 'Global Legends goal details page',
      },
      {
        src: '/images/progress-update-preview.png',
        alt: 'Global Legends progress update screen',
      },
    ],
  },
  {
    title: 'Global Leaderboard',
    description:
      'A leaderboard system encourages consistency through ranking, XP, and friendly competition.',
    images: [
      {
        src: '/images/leaderboard-preview.png',
        alt: 'Global Legends global leaderboard page',
      },
    ],
  },
  {
    title: 'Settings',
    description:
      'Manage profile details, account preferences, notifications, and appearance settings.',
    images: [
      {
        src: '/images/settings-preview.png',
        alt: 'Global Legends settings page showing profile, account, notifications, and appearance options',
      },
    ],
  },
  {
    title: 'Responsive Web App',
    description:
      'Designed to work smoothly across desktop, tablet, and mobile screens with a clean responsive layout.',
    images: [
      {
        src: '/images/responsive-web-preview.png',
        alt: 'Global Legends responsive web app preview across different screen sizes',
      },
    ],
  },
  {
    title: 'React Native Mobile App',
    description:
      'A mobile version currently in development, built with React Native to bring goal tracking and progress features to iOS and Android.',
    images: [
      {
        src: '/images/react-native-preview.png',
        alt: 'Global Legends React Native mobile app preview',
      },
    ],
  },
];

const features = [
  {
    title: 'Goal Tracking',
    description: 'Users can create goals, update progress, and track their consistency over time.',
  },
  {
    title: 'Progress Calendar',
    description:
      'A calendar-style view helps users visually understand their activity history and streaks.',
  },
  {
    title: 'Social Accountability',
    description:
      'Users can follow others, react to activity, and stay motivated through community interaction.',
  },
  {
    title: 'Leaderboard',
    description: 'A global leaderboard adds competition and gamification to encourage consistency.',
  },
  {
    title: 'User Profiles',
    description:
      'Each user has a profile showing goals, progress, followers, and personal activity.',
  },
  {
    title: 'Mobile Expansion',
    description: 'The platform is being expanded with React Native for Android and iOS support.',
  },
];

const techStack = [
  {
    category: 'Frontend',
    items: ['React', 'React Router', 'Axios', 'Custom CSS'],
  },
  {
    category: 'Mobile',
    items: ['React Native'],
  },
  {
    category: 'Backend',
    items: ['Java', 'Spring Boot', 'REST API', 'Spring Security'],
  },
  {
    category: 'Database',
    items: ['MySQL', 'JPA / Hibernate', 'Relational Database Design'],
  },
  {
    category: 'Testing & QA',
    items: [
      'JUnit',
      'Mockito',
      'Selenium WebDriver',
      'Postman',
      'Manual Testing',
      'Regression Testing',
      'Integration Testing',
    ],
  },
  {
    category: 'Tools',
    items: ['Git', 'Azure DevOps', 'Deployment', 'AI-assisted development'],
  },
];

const improvements = [
  'Improving page performance and reducing unnecessary API calls.',
  'Studying and documenting AI-assisted sections of the codebase.',
  'Preparing the React Native mobile version.',
  'Improving error handling, loading states, and user feedback.',
  'Expanding automated regression coverage with JUnit, Mockito, and Selenium WebDriver.',
];

const RecruitersPage = () => {
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);

  const activePreview = productPreviews[activePreviewIndex];

  const handleNextPreview = () => {
    setActivePreviewIndex((prevIndex) =>
      prevIndex === productPreviews.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const handlePreviousPreview = () => {
    setActivePreviewIndex((prevIndex) =>
      prevIndex === 0 ? productPreviews.length - 1 : prevIndex - 1,
    );
  };

  return (
    <main className="recruiters-page">
      {/* HERO */}
      <section className="recruiters-hero">
        <div className="recruiters-container recruiters-hero-grid">
          <div className="recruiters-hero-text">
            <p className="recruiters-eyebrow">Full-Stack Portfolio Project</p>

            <h1>Global Legends</h1>

            <h2>
              A social goal-tracking app built to help users stay consistent, accountable, and
              motivated.
            </h2>

            <p className="recruiters-hero-description">
              Global Legends is a full-stack web and mobile project built with React, React Native,
              Java Spring Boot, and a relational database. It combines goal tracking, progress
              history, social accountability, and gamified motivation into one platform.
            </p>

            <div className="recruiters-hero-actions">
              <a href="/" className="recruiters-primary-btn">
                View Live App
              </a>

              <a href="#tech-stack" className="recruiters-secondary-btn">
                View Tech Stack
              </a>
            </div>

            <div className="recruiters-hero-tags">
              <span>React</span>
              <span>React Native</span>
              <span>Spring Boot</span>
              <span>REST API</span>
              <span>Database Design</span>
            </div>
          </div>

          <div className="recruiters-hero-card">
            <img
              src="/images/dashboard-preview_3.png"
              alt="Global Legends profile page showing user rank, activity calendar, and public goals"
              className="recruiters-hero-screenshot"
            />
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="recruiters-section">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Overview</p>
            <h2>What is Global Legends?</h2>
          </div>

          <p className="recruiters-large-text">
            Global Legends is a gamified goal-tracking platform where users can create personal
            goals, track daily progress, view consistency over time, and interact with others
            through social features like following, reactions, and leaderboards.
          </p>

          <p className="recruiters-muted-text">
            The project was created to demonstrate real-world full-stack development, including
            frontend routing, backend API design, authentication, database relationships, protected
            routes, responsive UI, and deployment.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="recruiters-section recruiters-section-alt">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Features</p>
            <h2>Core app functionality</h2>
          </div>

          <div className="recruiters-card-grid">
            {features.map((feature) => (
              <article className="recruiters-card" key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TECH STACK */}
      <section className="recruiters-section" id="tech-stack">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Tech Stack</p>
            <h2>Technologies used</h2>
          </div>

          <div className="tech-stack-grid">
            {techStack.map((stack) => (
              <article className="tech-card" key={stack.category}>
                <h3>{stack.category}</h3>
                <div className="tech-tags">
                  {stack.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="development-note">
            <strong>Development note:</strong> This project was built with AI-assisted development.
            I used AI as a productivity and learning tool for planning, debugging, code explanation,
            and improving implementation. I actively review, test, modify, and study the code to
            understand and maintain the application.
          </div>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className="recruiters-section recruiters-section-alt">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Architecture</p>
            <h2>How the app is structured</h2>
          </div>

          <div className="architecture-box">
            <div className="architecture-step">React / React Native</div>
            <div className="architecture-arrow">↓</div>
            <div className="architecture-step">Spring Boot REST API</div>
            <div className="architecture-arrow">↓</div>
            <div className="architecture-step">Service Layer</div>
            <div className="architecture-arrow">↓</div>
            <div className="architecture-step">Repository Layer</div>
            <div className="architecture-arrow">↓</div>
            <div className="architecture-step">Relational Database</div>
          </div>

          <p className="recruiters-muted-text center-text">
            The frontend communicates with the Spring Boot backend through REST endpoints. The
            backend handles business logic, authentication, authorization, validation, and database
            operations.
          </p>
        </div>
      </section>

      {/* PRODUCT PREVIEWS */}
      <section className="recruiters-section" id="product-previews">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Product Previews</p>
            <h2>Social habit tracking in action</h2>
          </div>

          <div className="preview-carousel">
            <div>
              <div>
                <div
                  className={`preview-image-scroll ${
                    activePreview.images.length === 1 ? 'single-image' : ''
                  }`}
                >
                  {activePreview.images.map((image) => (
                    <div className="preview-image-card" key={image.src}>
                      <img src={image.src} alt={image.alt} className="preview-image" />
                    </div>
                  ))}
                </div>
                {activePreview.images.length > 1 && (
                  <p className="preview-scroll-hint">Scroll sideways to view more screenshots</p>
                )}
              </div>
            </div>

            <div className="preview-content">
              <p className="preview-counter">
                {activePreviewIndex + 1} / {productPreviews.length}
              </p>

              <h3>{activePreview.title}</h3>
              <p>{activePreview.description}</p>

              <div className="preview-controls">
                <button type="button" onClick={handlePreviousPreview}>
                  Previous
                </button>

                <button type="button" onClick={handleNextPreview}>
                  Next
                </button>
              </div>

              <div className="preview-dots">
                {productPreviews.map((preview, index) => (
                  <button
                    key={preview.title}
                    type="button"
                    className={index === activePreviewIndex ? 'active' : ''}
                    onClick={() => setActivePreviewIndex(index)}
                    aria-label={`Show ${preview.title}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="recruiters-section recruiters-section-alt">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Problem Solving</p>
            <h2>Challenges solved</h2>
          </div>

          <div className="challenge-list">
            <div>
              <h3>API Design</h3>
              <p>
                Built REST endpoints for goals, progress tracking, user data, social reactions, and
                leaderboard-related features.
              </p>
            </div>

            <div>
              <h3>Database Relationships</h3>
              <p>
                Designed relationships between users, goals, progress entries, profiles, followers,
                and activity reactions.
              </p>
            </div>

            <div>
              <h3>Frontend State and UI</h3>
              <p>
                Managed loading states, conditional rendering, protected routes, modals, profile
                data, and responsive layouts.
              </p>
            </div>

            <div>
              <h3>Performance Improvements</h3>
              <p>
                Investigated slow pages by analyzing API calls, large responses, frontend rendering,
                and unnecessary repeated requests.
              </p>
            </div>
            <div>
  <h3>Testing & Quality Assurance</h3>
  <p>
    Tested backend APIs with Postman, wrote Java tests with JUnit and Mockito,
    and built Selenium WebDriver tests for critical user workflows such as
    authentication, goal creation, and goal management.
  </p>
</div>
          </div>
        </div>
      </section>

      {/* IMPROVEMENTS */}
      <section className="recruiters-section">
        <div className="recruiters-container">
          <div className="section-heading">
            <p className="section-kicker">Current Work</p>
            <h2>What I am improving next</h2>
          </div>

          <ul className="improvement-list">
            {improvements.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT */}
      <section className="recruiters-section recruiters-contact-section">
        <div className="recruiters-container recruiters-contact-card">
          <p className="section-kicker">Contact</p>
          <h2>Interested in discussing the project?</h2>

          <p>
            I am open to junior developer, QA/testing, IT, and software-related opportunities where
            I can keep improving as a developer and contribute to real products.
          </p>

          <div className="recruiters-hero-actions">
            <a href="mailto:rustom1231@gmail.com" className="recruiters-primary-btn">
              Email Me
            </a>

            <a href="/" className="recruiters-secondary-btn">
              Back to App
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RecruitersPage;
